import React, { useEffect, useRef, useState } from "react";
import backendApiService from "../service/backend-api.service";
import { isValidCustomerEmail } from "../validation/customerEmail";
import PersonalDataConsent from "../legal/PersonalDataConsent";
import { LOGIN_CAPTCHA, sendWithCaptcha, useCaptcha } from "../visoria/captcha";
import CaptchaNotice from "../visoria/components/CaptchaNotice";
import { reachGoal } from "../utils/metrika";
import { useEnterSubmit } from "../hooks/useEnterSubmit";

const CODE_LENGTH = 6;

function useCountdown() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timerId = window.setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => window.clearTimeout(timerId);
  }, [seconds]);
  return [seconds, setSeconds];
}

function AccountLogin({ onLoggedIn }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [needsConsent, setNeedsConsent] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useCountdown();

  const [codeRequests, setCodeRequests] = useState(0);
  const captchaEnabled = useCaptcha(true, { ...LOGIN_CAPTCHA, refreshKey: codeRequests });

  const sendCode = async () => {
    setBusy(true);
    setError("");
    try {
      await sendWithCaptcha(
        (captchaToken) => backendApiService.requestLoginCode(email.trim(), captchaToken),
        undefined,
        LOGIN_CAPTCHA,
      );
      setCodeRequests((count) => count + 1);
      setStep("code");
      setCode("");
      setResendIn(60);
    } catch (requestError) {
      if (requestError.status === 429 && requestError.retryAfter) {
        // Код уже недавно отправлен и ещё действует — его можно ввести.
        setResendIn(requestError.retryAfter);
        setStep("code");
      } else {
        setError(requestError.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEmailSubmit = (event) => {
    event.preventDefault();
    if (!isValidCustomerEmail(email)) {
      setError("Укажите корректный email");
      return;
    }
    sendCode();
  };

  const canSubmit =
    code.length === CODE_LENGTH && (!needsConsent || consent) && !busy;

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      const result = await backendApiService.verifyLoginCode(email.trim(), code, consent);
      if (result.needsConsent) {
        setNeedsConsent(true);
        return;
      }
      reachGoal(needsConsent ? "account_created" : "account_login");
      onLoggedIn(result.account);
    } catch (verifyError) {
      setError(verifyError.message);
      if (verifyError.code === "expired" || verifyError.code === "too_many") {
        setCode("");
      }
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = () => {
    setStep("email");
    setCode("");
    setNeedsConsent(false);
    setConsent(false);
    setError("");
  };

  const codeFormRef = useRef(null);
  useEnterSubmit(codeFormRef, step === "code" && canSubmit);

  if (step === "email") {
    return (
      <form className="account-form" onSubmit={handleEmailSubmit} noValidate>
        <p className="other-store-description">
          Войдите по почте, на которую оформляете заказы: в кабинете будут все
          заказы и коды на эту почту, в том числе прошлые.
        </p>
        <label className="other-store-field">
          <span className="other-store-field-label">Почта</span>
          <input
            type="email"
            className="other-store-input ym-disable-keys"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@mail.ru"
            autoComplete="email"
            autoFocus
          />
        </label>
        {error ? <p className="other-store-error">{error}</p> : null}
        <button type="submit" className="other-store-buy-btn" disabled={busy || !email.trim()}>
          {busy ? "Отправляем…" : "Получить код"}
        </button>
        {captchaEnabled ? <CaptchaNotice /> : null}
      </form>
    );
  }

  return (
    <form ref={codeFormRef} className="account-form" onSubmit={handleCodeSubmit} noValidate>
      <p className="other-store-description">
        Отправили 6-значный код на <strong>{email.trim().toLowerCase()}</strong>.
        Код действует 10 минут. Если письма нет, проверьте папку «Спам».
      </p>
      <label className="other-store-field">
        <span className="other-store-field-label">Код из письма</span>
        <input
          type="text"
          className="other-store-input account-code-input ym-disable-keys"
          value={code}
          onChange={(event) =>
            setCode(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))
          }
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          placeholder="000000"
          autoFocus
        />
      </label>
      {needsConsent ? (
        <div className="account-consent-box">
          <p className="other-store-description">
            На эту почту ещё нет кабинета. Чтобы создать его, отметьте согласие.
          </p>
          <PersonalDataConsent
            checked={consent}
            onChange={setConsent}
            className="other-store-consent"
            linkClassName="other-store-consent-link"
          />
        </div>
      ) : null}
      {error ? <p className="other-store-error">{error}</p> : null}
      <button type="submit" className="other-store-buy-btn" disabled={!canSubmit}>
        {busy ? "Проверяем…" : needsConsent ? "Создать кабинет" : "Войти"}
      </button>
      <div className="account-form-links">
        <button
          type="button"
          className="account-link-btn"
          onClick={sendCode}
          disabled={busy || resendIn > 0}
        >
          {resendIn > 0
            ? `Отправить код ещё раз через ${resendIn} с`
            : "Отправить код ещё раз"}
        </button>
        <button type="button" className="account-link-btn" onClick={changeEmail}>
          Изменить почту
        </button>
      </div>
      {captchaEnabled ? <CaptchaNotice /> : null}
    </form>
  );
}

export default AccountLogin;
